import { motion } from "framer-motion";
import {
  Scissors,
  User,
  Palette,
  Wind,
  Sparkles,
  Crown,
  Baby,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SALON_PHOTOS } from "../data/assets";

const ICONS = [Scissors, User, Palette, Wind, Sparkles, Crown, Baby];

export const Services = () => {
  const { t } = useLang();

  return (
    <section
      id="services"
      className="relative py-24 md:py-36 overflow-hidden"
      data-testid="services-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${SALON_PHOTOS.services})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/95 to-[#050505]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-16 md:mb-24"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-10 bg-[#C5A059]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#C5A059]">
              {t.services.eyebrow}
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
            {t.services.title}
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/60 font-light max-w-xl">
            {t.services.subtitle}
          </p>
        </motion.div>

        {/* Grid */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.08 } },
          }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5"
        >
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i] || Scissors;
            return (
              <motion.article
                key={i}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
                }}
                whileHover={{ y: -4 }}
                className="group relative bg-[#0a0a0a] hover:bg-[#0f0f0f] p-8 md:p-10 transition-colors duration-500 cursor-default"
                data-testid={`service-card-${i}`}
              >
                {/* Icon */}
                <div className="mb-8 flex items-center justify-between">
                  <div className="w-14 h-14 rounded-full border border-[#C5A059]/30 flex items-center justify-center group-hover:border-[#C5A059] group-hover:bg-[#C5A059]/5 transition-all duration-500">
                    <Icon size={22} strokeWidth={1.5} className="text-[#C5A059]" />
                  </div>
                  <span className="font-display italic text-white/15 text-3xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="font-display text-xl md:text-2xl text-white mb-3 leading-tight">
                  {s.name}
                </h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  {s.desc}
                </p>

                {/* Bottom gold line */}
                <div className="mt-8 h-px w-8 bg-[#C5A059]/40 group-hover:w-16 transition-all duration-500" />
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
